/**
 * Statistics Panel
 * 
 * Displays real-time statistics for the Live Location Activity Dashboard:
 * - Events in queue
 * - Events last 5 minutes
 * - Active operators
 * - Active locations
 * - Current rack being displayed
 * 
 * Updates every second with trend indicators (↑ ↓ →)
 * 
 * @author BizClick WMS
 * @version 1.0 - Fase 7 Implementation
 */

import React, { useState, useEffect } from 'react';
import { 
  Activity, 
  Users, 
  MapPin, 
  Clock, 
  TrendingUp, 
  TrendingDown, 
  Minus,
  Package
} from 'lucide-react';
import { Card } from '../../../common/Card';
import { LocationActivityEvent } from '../../../../types/locationActivity';
import { QueueStats } from './types';

interface StatisticsPanelProps {
  /** All cached events for calculations */
  allEvents: LocationActivityEvent[];
  
  /** Current queue statistics */
  queueStats: QueueStats | null;
  
  /** Currently displayed rack number (as string to preserve leading zeros) */
  currentRack: string | null;
  
  /** Current warehouse ID */
  currentWarehouseId: string | null;
  
  /** Update interval in milliseconds (default: 1000ms) */
  updateInterval?: number;
  
  /** Trend calculation window in milliseconds (default: 10000ms) */
  trendWindow?: number;
}

interface StatItem {
  label: string;
  value: string | number;
  icon: React.ComponentType<any>;
  color: string;
  trend?: 'up' | 'down' | 'stable';
  trendValue?: number;
}

/**
 * Calculate trend for a metric by comparing current vs previous period
 */
const calculateTrend = (current: number, previous: number): 'up' | 'down' | 'stable' => {
  const threshold = 0.05; // 5% change threshold
  const change = (current - previous) / (previous || 1);
  
  if (change > threshold) return 'up';
  if (change < -threshold) return 'down';
  return 'stable';
};

/**
 * Get trend icon based on direction
 */
const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return TrendingUp;
    case 'down':
      return TrendingDown;
    case 'stable':
      return Minus;
  }
};

/**
 * Get trend color based on direction
 */
const getTrendColor = (trend: 'up' | 'down' | 'stable') => {
  switch (trend) {
    case 'up':
      return 'text-green-600';
    case 'down':
      return 'text-red-600';
    case 'stable':
      return 'text-gray-400';
  }
};

export const StatisticsPanel: React.FC<StatisticsPanelProps> = ({
  allEvents,
  queueStats,
  currentRack,
  currentWarehouseId,
  updateInterval = 1000,
  trendWindow = 10000
}) => {
  const [stats, setStats] = useState<StatItem[]>([]);
  const [previousStats, setPreviousStats] = useState<Map<string, number>>(new Map());

  /**
   * Calculate all statistics
   */
  const calculateStatistics = (): StatItem[] => {
    const now = Date.now();
    const fiveMinutesAgo = now - 5 * 60 * 1000;

    // Events in last 5 minutes
    const recentEvents = allEvents.filter(event => {
      const eventTime = new Date(event.timestamp).getTime();
      return eventTime >= fiveMinutesAgo;
    });

    // Active operators (unique operator IDs in last 5 minutes)
    const activeOperators = new Set(recentEvents.map(e => e.operatorId)).size;

    // Active locations (unique bin codes in last 5 minutes)
    const activeLocations = new Set(recentEvents.map(e => e.binCode)).size;

    // Events in queue
    const eventsInQueue = queueStats?.totalEvents || 0;
    const racksInQueue = queueStats?.queueLength || 0;

    // Previous values for trend calculation
    const prevEventsLast5Min = previousStats.get('eventsLast5Min') || 0;
    const prevActiveOperators = previousStats.get('activeOperators') || 0;
    const prevActiveLocations = previousStats.get('activeLocations') || 0;
    const prevEventsInQueue = previousStats.get('eventsInQueue') || 0;

    // Calculate trends
    const eventsLast5MinTrend = calculateTrend(recentEvents.length, prevEventsLast5Min);
    const operatorsTrend = calculateTrend(activeOperators, prevActiveOperators);
    const locationsTrend = calculateTrend(activeLocations, prevActiveLocations);
    const queueTrend = calculateTrend(eventsInQueue, prevEventsInQueue);

    // Update previous stats for next calculation
    setPreviousStats(new Map([
      ['eventsLast5Min', recentEvents.length],
      ['activeOperators', activeOperators],
      ['activeLocations', activeLocations],
      ['eventsInQueue', eventsInQueue]
    ]));

    return [
      {
        label: 'Reoler i kø',
        value: racksInQueue,
        icon: Package,
        color: 'text-purple-600',
        trend: queueTrend,
        trendValue: eventsInQueue
      },
      {
        label: 'Hendelser siste 5 min',
        value: recentEvents.length,
        icon: Activity,
        color: 'text-blue-600',
        trend: eventsLast5MinTrend,
        trendValue: recentEvents.length
      },
      {
        label: 'Aktive operatører',
        value: activeOperators,
        icon: Users,
        color: 'text-green-600',
        trend: operatorsTrend,
        trendValue: activeOperators
      },
      {
        label: 'Aktive lokasjoner',
        value: activeLocations,
        icon: MapPin,
        color: 'text-orange-600',
        trend: locationsTrend,
        trendValue: activeLocations
      }
    ];
  };

  /**
   * Update statistics at regular intervals
   */
  useEffect(() => {
    // Initial calculation
    setStats(calculateStatistics());

    // Set up interval for updates
    const interval = setInterval(() => {
      setStats(calculateStatistics());
    }, updateInterval);

    return () => clearInterval(interval);
  }, [allEvents, queueStats, updateInterval]);

  return (
    <Card className="p-4">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2 pb-3 border-b border-gray-200">
          <Activity className="text-blue-600" size={20} />
          <h3 className="text-lg font-semibold text-gray-900">Live Statistikk</h3>
        </div>

        {/* Current Rack Display */}
        {currentRack && (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="text-blue-600" size={20} />
                <div>
                  <div className="text-xs text-gray-600 uppercase tracking-wide">Aktuell reol</div>
                  <div className="text-2xl font-bold text-gray-900">Reol {currentRack}</div>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {currentWarehouseId}
              </div>
            </div>
          </div>
        )}

        {/* Statistics Grid */}
        <div className="grid grid-cols-1 gap-3">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const TrendIcon = stat.trend ? getTrendIcon(stat.trend) : null;
            const trendColor = stat.trend ? getTrendColor(stat.trend) : '';

            return (
              <div
                key={index}
                className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg bg-gray-50 ${stat.color}`}>
                      <Icon size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 uppercase tracking-wide">
                        {stat.label}
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mt-1">
                        {stat.value}
                      </div>
                    </div>
                  </div>
                  
                  {/* Trend Indicator */}
                  {TrendIcon && stat.trend !== 'stable' && (
                    <div className={`flex items-center gap-1 ${trendColor}`}>
                      <TrendIcon size={16} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Last Update Timestamp */}
        <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-200">
          <Clock size={12} />
          <span>Oppdatert: {new Date().toLocaleTimeString('nb-NO')}</span>
        </div>

        {/* Info Box */}
        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
          <div className="text-xs text-blue-800">
            <p className="font-medium mb-1">ℹ️ Statistikk Info</p>
            <p>
              Statistikken oppdateres hvert sekund og viser sanntidsdata fra lageret.
              Trendpilene indikerer om aktiviteten øker (↑), synker (↓) eller er stabil (→).
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default StatisticsPanel;
